import {
  Vector3,
  Quaternion,
  Matrix4,
  Euler,
  Object3D,
  MathUtils,
  Mesh,
} from "three";
import { ArtworkData } from "../renderer/artworks";

export enum ContentState {
  HIDDEN,
  INTRO_ANIM,
  OUTRO_ANIM,
  PORTAL,
  CONTENT_IN_TRANSITION,
  CONTENT_OUT_TRANSITION,
}

class ContentStateManager {
  private contentContainer: Object3D | null = null;
  private currentState: ContentState = ContentState.HIDDEN;
  private artworkActive: ArtworkData | null = null;
  private initialMatrix: Matrix4 = new Matrix4();
  private firstDetectedFace: string | null = null;

  private lerpActive: boolean = false;
  private lerpStartTime: number = 0;
  private lerpDuration: number = 0;
  private startQuat: Quaternion = new Quaternion();
  private targetQuat: Quaternion = new Quaternion();
  private lastRotatedFace: string | null = null;
  public init(container: Object3D) {
    this.contentContainer = container;
  }

  public getActiveArtwork(): ArtworkData | null {
    return this.artworkActive;
  }

  public setActiveArtwork(artwork: ArtworkData | null) {
    this.artworkActive = artwork;
  }

  public getState(): ContentState {
    return this.currentState;
  }

  public setState(newState: ContentState) {
    this.currentState = newState;

    if (this.contentContainer) {
      this.updateContentVisibility();
    }
  }

  public setFirstDetectedFace(targetName: string) {
    if (!this.firstDetectedFace) {
      this.firstDetectedFace = targetName;
    }
  }

  public resetFirstDetectedFace() {
    this.firstDetectedFace = null;
  }

  public getFirstDetectedFace(): string | null {
    return this.firstDetectedFace;
  }

  public getLastRotatedFace(): string | null {
    return this.lastRotatedFace;
  }

  public orientContent(shouldLerp = false) {
    if (!this.artworkActive) return;

    if (!this.firstDetectedFace) {
      this.firstDetectedFace = this.artworkActive.targetName;
    }

    const faceNames = ["cube_A", "cube_B", "cube_C", "cube_D", "cube_E"];
    const firstFaceIndex = faceNames.indexOf(this.firstDetectedFace);
    if (firstFaceIndex === -1) {
      console.warn(`Unexpected first detected face: ${this.firstDetectedFace}`);
      return;
    }

    const rotationMap: Record<string, Euler> = {};
    faceNames.forEach((face, index) => {
      const relativeRotation = ((index - firstFaceIndex) * -Math.PI) / 2;
      rotationMap[face] =
        face === "cube_E"
          ? new Euler(Math.PI / 2, relativeRotation, 0)
          : new Euler(0, relativeRotation, 0);
    });

    const targetQuat = new Quaternion();
    if (this.artworkActive.targetName in rotationMap) {
      targetQuat.setFromEuler(rotationMap[this.artworkActive.targetName]);
    }

    if (this.contentContainer) {
      const scale = 0.8;
      const posVec = new Vector3(0, 0, -0.5 * scale);

      if (shouldLerp ) {
        this.startOrientationLerp(targetQuat, 1000);
      } else {
        this.contentContainer.quaternion.copy(targetQuat);
        this.initialMatrix.compose(
          posVec,
          targetQuat,
          new Vector3(scale, scale, scale)
        );
        if (!this.contentContainer.matrixAutoUpdate) {
          this.contentContainer.matrix.copy(this.initialMatrix);
        }
        this.lastRotatedFace = this.artworkActive.targetName; 

      }
    }
  }

  public startOrientationLerp(targetQuat: Quaternion, duration: number) {
    if (!this.contentContainer) return;

    if (this.contentContainer.quaternion.angleTo(targetQuat) < 0.1) {
      console.log("Skipping lerp, no rotation needed.");
      return;
    }
    this.lerpActive = true;
    this.lerpStartTime = performance.now();
    this.lerpDuration = duration;
    this.startQuat = this.contentContainer.quaternion.clone();
    this.targetQuat = targetQuat.clone();
  }

  public updateOrientationLerp() {
    if (!this.lerpActive || !this.contentContainer) return;

    const elapsed = performance.now() - this.lerpStartTime;
    let progress = elapsed / this.lerpDuration;
    progress = Math.min(progress, 1);

    const lerpedQuat = new Quaternion();
    lerpedQuat.slerpQuaternions(this.startQuat, this.targetQuat, progress);
    this.contentContainer.quaternion.copy(lerpedQuat);

    const scale = 0.8;
    const posVec = new Vector3(0, 0, -0.5 * scale);
    this.initialMatrix.compose(
      posVec,
      lerpedQuat,
      new Vector3(scale, scale, scale)
    );
    if (!this.contentContainer.matrixAutoUpdate) {
      this.contentContainer.matrix.copy(this.initialMatrix);
    }

    if (progress >= 1) {
      this.lerpActive = false;
    }
  }

  public applyTrackingMatrix(transformation: {
    position: Vector3;
    rotation: Quaternion;
    scale: number;
  }) {
    if (!this.contentContainer) return;

    const { position, rotation, scale } = transformation;
    const normalizedRotation = new Quaternion(
      rotation.x,
      rotation.y,
      rotation.z,
      rotation.w
    ).normalize();

    const trackingMatrix = new Matrix4();
    trackingMatrix.compose(
      position,
      normalizedRotation,
      new Vector3(scale, scale, scale)
    );
    trackingMatrix.multiply(this.initialMatrix);

    this.contentContainer.matrix.copy(trackingMatrix);
    this.contentContainer.matrix.decompose(
      this.contentContainer.position,
      this.contentContainer.quaternion,
      this.contentContainer.scale
    );

    this.contentContainer.visible = true;
  }

  private updateContentVisibility() {
    if (!this.contentContainer) return;

    this.contentContainer.traverse((obj) => {
      if (!(obj as Mesh).isMesh) return;
      const isCube = obj.name === "cubeFront" || obj.name === "cubeBack";
      const isAnimIn =
        obj.name === "animContentIn" ||
        obj.parent?.name === "IN";
      const isAnimOut =
        obj.name === "animContentOut" ||
        obj.parent?.name === "OUT";

      obj.visible = false;

      switch (this.currentState) {
        case ContentState.PORTAL:
          if (isCube) obj.visible = true;
          break;
        case ContentState.INTRO_ANIM:
          if (isAnimIn) obj.visible = true;
          break;
        case ContentState.OUTRO_ANIM:
          if (isAnimOut) obj.visible = true;
          break;
        case ContentState.CONTENT_IN_TRANSITION:
          if (isAnimOut || isCube) obj.visible = true;
          break;
        case ContentState.CONTENT_OUT_TRANSITION:
          if (isAnimIn || isCube) obj.visible = true;
          break;
        case ContentState.HIDDEN:
        default:
          break;
      }
    });
  }

  public animateScale(
    objects: Object3D | Object3D[],
    animationState: "in" | "out",
    duration = 2000
  ) {
    let arrayObj: Object3D[] = [];
    if (!Array.isArray(objects)) {
      arrayObj = [objects];
    }

    const startScale = animationState === "in" ? 0 : 1;
    const targetScale = animationState === "in" ? 1 : 0;
    const startTime = performance.now();

    function scaleStep() {
      const elapsed = performance.now() - startTime;
      let progress = elapsed / duration;
      progress = Math.min(progress, 1);

      const scaleFactor = MathUtils.lerp(startScale, targetScale, progress);
      arrayObj.forEach((obj) => {
        if (obj instanceof Object3D) {
          obj.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }
      });

      if (progress < 1) {
        requestAnimationFrame(scaleStep);
      }
    }

    arrayObj.forEach((obj) =>
      obj.scale.set(startScale, startScale, startScale)
    );
    requestAnimationFrame(scaleStep);
  }
}

export const contentStateManager = new ContentStateManager();
